import { useEffect, useMemo, useRef } from 'react';
import { useStudioStore } from '../../stores/studioStore';
import { useMixerStore } from '../../stores/mixerStore';
import { ChannelFader } from './ChannelFader';
import { ChannelInspector } from './ChannelInspector';
import { MasterBusPanel } from './MasterBusPanel';
import { clsx } from '../../lib/utils';

interface MixerViewProps {
  compact?: boolean;
}

export function MixerView({ compact }: MixerViewProps) {
  const tracks = useStudioStore((s) => s.tracks);
  const selectedTrackId = useStudioStore((s) => s.selectedTrackId);
  const selectTrack = useStudioStore((s) => s.selectTrack);
  const mode = useMixerStore((s) => s.mode);
  const setMode = useMixerStore((s) => s.setMode);
  const inspectorOpen = useMixerStore((s) => s.inspectorOpen);
  const setInspectorOpen = useMixerStore((s) => s.setInspectorOpen);
  const inspectorWidth = useMixerStore((s) => s.inspectorWidth);
  const setInspectorWidth = useMixerStore((s) => s.setInspectorWidth);
  const fullscreen = useMixerStore((s) => s.fullscreen);
  const toggleFullscreen = useMixerStore((s) => s.toggleFullscreen);
  const meters = useMixerStore((s) => s.meters);
  const hydrateMeters = useMixerStore((s) => s.hydrateMeters);

  const railRef = useRef<HTMLDivElement>(null);

  useEffect(() => hydrateMeters(), [hydrateMeters]);

  const channelTracks = useMemo(
    () => tracks.filter((t) => t.type !== 'master'),
    [tracks],
  );
  const master = useMemo(() => tracks.find((t) => t.type === 'master') || null, [tracks]);
  const selected = useMemo(
    () => tracks.find((t) => t.id === selectedTrackId) || null,
    [tracks, selectedTrackId],
  );

  return (
    <div
      className={clsx(
        'flex h-full min-h-0 flex-col bg-ssl-graphite text-ssl-text',
        fullscreen && 'fixed inset-0 z-50',
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-ssl-metal/50 px-2 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ssl-accent">
          Melodin Console
        </span>
        <div className="ml-2 flex rounded border border-ssl-metal/50 p-0.5">
          {(['simple', 'studio'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={clsx(
                'rounded px-2 py-0.5 text-[10px] capitalize',
                mode === m ? 'bg-ssl-accent/20 text-ssl-accent' : 'text-ssl-muted',
              )}
            >
              {m}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setInspectorOpen(!inspectorOpen)}
          className="rounded border border-ssl-metal/50 px-2 py-0.5 text-[10px] text-ssl-muted hover:text-ssl-text"
        >
          {inspectorOpen ? 'Hide Strip' : 'Show Strip'}
        </button>
        <button
          type="button"
          onClick={toggleFullscreen}
          className="rounded border border-ssl-metal/50 px-2 py-0.5 text-[10px] text-ssl-muted hover:text-ssl-text"
        >
          {fullscreen ? 'Exit Full' : 'Full Screen'}
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            ref={railRef}
            className={clsx(
              'flex min-h-0 flex-1 gap-1 overflow-x-auto overflow-y-hidden px-2 py-2',
              compact ? 'items-stretch' : 'items-end',
            )}
          >
            {channelTracks.map((t) => (
              <ChannelFader
                key={t.id}
                track={t}
                selected={selectedTrackId === t.id}
                meters={meters[t.id]}
                compact={compact}
                onSelect={() => {
                  selectTrack(t.id);
                  if (!inspectorOpen) setInspectorOpen(true);
                }}
              />
            ))}
            {master && (
              <ChannelFader
                track={master}
                selected={selectedTrackId === master.id}
                meters={meters[master.id] || meters.__master}
                compact={compact}
                onSelect={() => {
                  selectTrack(master.id);
                  if (!inspectorOpen) setInspectorOpen(true);
                }}
              />
            )}
          </div>
          <MasterBusPanel master={master} compact={compact} />
        </div>

        {inspectorOpen && (
          <aside
            className="relative flex shrink-0 flex-col border-l border-ssl-metal/50"
            style={{ width: inspectorWidth }}
          >
            <div
              className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize hover:bg-ssl-accent/40"
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startW = inspectorWidth;
                const onMove = (ev: MouseEvent) => {
                  setInspectorWidth(startW - (ev.clientX - startX));
                };
                const onUp = () => {
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
            />
            <ChannelInspector track={selected} />
          </aside>
        )}
      </div>
    </div>
  );
}
